import AuthPage from "@/features/Auth/pages/AuthPage";
import ChatBotPage from "@/features/ChatBot/pages/ChatBotPage";
import MedicalAssistancePage from "@/features/ChatForMedicalAssistance/pages/MedicalAssistancePage";
import AppUpdateHandler from "@/features/Common/components/AppUpateHandler";
import CollectUserQueryFlow from "@/features/Common/components/CollectUserQuery";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import GetHelpModal from "@/features/Common/components/GetHelpModal";
import HandleExpiredSession from "@/features/Common/components/HandleExpiredSession";
import MaxWidthContainer from "@/features/Common/components/MaxWidthContainer";
import PaymentFailure from "@/features/Common/components/PaymentFailure";
import RedirectHandler from "@/features/Common/components/RedirectHandler";
import RedirectIfAuthenticated from "@/features/Common/components/RedirectIfAuthenticated";
import RedirectIfNotAuthenticated from "@/features/Common/components/RedirectIfNotAuthenticated";
import RedirectIfNotB2BMember from "@/features/Common/components/RedirectIfNotB2BMember";
import RedirectIfNotRetailMember from "@/features/Common/components/RedirectIfNotRetailMember";
import SuspenseFallback from "@/features/Common/components/SuspenseFallback";
import MaintenancePage from "@/features/Common/pages/MaintenancePage";
import useMaintenanceConfig from "@/features/Common/hooks/useMaintenanceConfig";
import MedicalAssistanceUnsupportedPage from "@/features/Common/pages/MedicalAssistanceUnsupportedPage";
import NotFoundPage from "@/features/Common/pages/NotFoundPage";
import DashboardPage from "@/features/Dashboard/pages/DashboardPage";
import BinahScanFlow from "@/features/HealthCheck/components/BinahScanFlow";
import BinahScanFlowNative from "@/features/HealthCheck/components/BinahScanFlowNative";
import InterCareMedicalAssistanceFlow from "@/features/MedicalConsultations/components/IntercareMedicalAssistance";
import ThirdPartyMedicalAssistanceFlow from "@/features/MedicalConsultations/components/ThirdPartyMedicalAssistanceFlow";
import MemberRequiredActionsPage from "@/features/MemberRequiredActions/pages/MemberRequiredActionsPage";
import MembershipContactDetailsPage from "@/features/Membership/pages/MembershipContactDetailsPage";
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- retained for commented-out route below, pending restoration
import MembershipDetailPage from "@/features/Membership/pages/MembershipDetailPage";
import MembershipManagementPage from "@/features/Membership/pages/MembershipManagementPage";
import MembershipPolicyDocumentsPage from "@/features/Membership/pages/MembershipPolicyDocumentsPage";
import MembershipTermsPage from "@/features/Membership/pages/MembershipTermsPage";
import MessageDetailsPage from "@/features/Messages/pages/MessageDetailsPage";
import MessagesPage from "@/features/Messages/pages/MessagesPage";
import GetSupport from "@/features/More/components/GetSupport";
import AboutAppPage from "@/features/More/pages/AboutAppPage";
import HealthKitDebugPage from "@/features/More/pages/HealthKitDebugPage";
import CommunicationPreferencesPage from "@/features/More/pages/CommunicationPreferencesPage";
import MorePage from "@/features/More/pages/MorePage";
import MyPurchasesPage from "@/features/More/pages/MyPurchasesPage";
import OpenSourceLicensesPage from "@/features/More/pages/OpenSourceLicensesPage";
import MyBenefitsPage from "@/features/MyBenefits/pages/MyBenefitsPage";
import MyHealthPage from "@/features/MyHealth/pages/MyHealthPage";
import MyHealthProfilePage from "@/features/MyHealth/pages/MyHealthProfilePage";
import MyHealthRecordPage from "@/features/MyHealth/pages/MyHealthRecordPage";
import HealthMeasurementDetailPage from "@/features/MyHealth/pages/MyHealthPage/HealthMeasurementDetailPage";
import HealthMeasurementPage from "@/features/MyHealth/pages/MyHealthPage/HealthMeasurementPage";
import MyProfilePage from "@/features/MyProfile/pages/MyProfilePage";
import PathologyFlow from "@/features/Pathology/components/PathologyFlow";
import PaymentRedirect from "@/features/Common/components/PaymentRedirect";
import PathologyResultsBreakoutPage from "@/features/Pathology/pages/PathologyResultsBreakoutPage";
import UnsubscribePage from "@/features/Unsubscribe/pages/UnsubscribePage";
import VoucheringFlow from "@/features/Vouchering/components/VoucheringFlow";
import WelcomePage from "@/features/Welcome/pages/WelcomePage";
import { trackPageView } from "@/services/analytics-adapter";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { maxWidthConfig } from "@/theme";
import {
  lazy,
  LazyExoticComponent,
  Suspense,
  useEffect,
  useState,
} from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import MyCareCardVouchersPage from "@/features/More/pages/MyCareCardVouchersPage";
import MyDiscountVouchersPage from "@/features/More/pages/MyDiscountVouchersPage";
import MyContactDetailsPage from "@/features/More/pages/MyContactDetailsPage";
import MyProfileAndMembershipPage from "@/features/MyProfile/pages/MyProfileAndMembershipPage";
import HealthScoreLayout from "@/features/HealthScore/layout/HealthScoreLayout";
import HealthScorePage from "@/features/HealthScore/pages/HealthScorePage";
import LifeStyleQuestionnaire from "@/features/HealthScore/pages/LifestyleQuestionnaire";
import HealthHistoryQuestionnaire from "@/features/HealthScore/pages/HealthHistoryQuestionnaire";
import MentalWellbeingQuestionnaire from "@/features/HealthScore/pages/MentalWellbeingQuestionnaire";
import BloodPressureManualEntry from "@/features/HealthScore/pages/BloodPressureManualEntry";
import HeartRateManualEntry from "@/features/HealthScore/pages/HeartRateManualEntry";
import BreathingRateManualEntry from "@/features/HealthScore/pages/BreathingRateManualEntry";

interface PlaygroundPageProps {
  changeMaxWidth: (arg: string) => void;
}
interface PublicBinahScanFlowProps {
  onCompletedFlow?: () => void;
}

let PlaygroundPage: LazyExoticComponent<React.FC<PlaygroundPageProps>> | null =
  null;

const PublicBinahScanFlow: LazyExoticComponent<
  React.FC<PublicBinahScanFlowProps>
> = lazy(() => import("@/features/HealthCheck/components/PublicBinahScanFlow"));

if (isFeatureEnabled(FeatureToggleIdentifier.SHOW_PLAYGROUND)) {
  PlaygroundPage = lazy(
    () => import("../features/Playground/pages/PlaygroundPage")
  );
}

let previousLocation = "";

const isTrendedVitalsEnabled = isFeatureEnabled(
  FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
);

export default function Router() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMaintenanceMode } = useMaintenanceConfig();
  const locationState = location.state as {
    from?: string | { pathname: string; search?: string; hash?: string };
    isRedirected?: boolean;
  };
  const from = locationState?.from;
  const isFromPath = from && typeof from === "string" && from.startsWith("/");
  const isFromLocation =
    from &&
    typeof from === "object" &&
    typeof (from as { pathname?: string }).pathname === "string";
  const routesLocation = isFromPath
    ? (from as string)
    : isFromLocation
      ? (from as { pathname: string; search?: string; hash?: string })
      : location;
  const [maxWidth, setMaxWidth] = useState(maxWidthConfig.defaultMaxWidth);

  useEffect(() => {
    trackPageView({
      "page.url": location.pathname,
      "page.sourceUrl": previousLocation,
      other: { ...location.state },
    });
    previousLocation = location.pathname + location.search;
  }, [location]);

  function changeMaxWidth(maxWidth: string) {
    setMaxWidth(maxWidth);
  }

  const isUnifiedCareEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_UNIFIED_CARE
  );

  const communicationPreferencesUrl = isUnifiedCareEnabled
    ? "/home/more/communication-preferences"
    : "/home/my-health/communication-preferences";
  const myContactDetailsUrl = isUnifiedCareEnabled
    ? "/home/more/contact-details"
    : "/home/my-health/contact-details";
  const myContactPage = isUnifiedCareEnabled ? (
    <MyContactDetailsPage />
  ) : (
    <MyHealthPage />
  );

  return (
    <ScrollToTop>
      <MaxWidthContainer maxWidth={maxWidth}>
        <AppUpdateHandler>
          <RedirectHandler>
            {isMaintenanceMode ? (
              <Routes>
                <Route path="*" element={<MaintenancePage />} />
                <Route
                  path="/get-help/log-query"
                  element={<CollectUserQueryFlow />}
                />
                <Route
                  path="/unsubscribe"
                  element={<UnsubscribePage changeMaxWidth={changeMaxWidth} />}
                />
              </Routes>
            ) : (
              <>
                <Routes location={routesLocation}>
                  <Route
                    index
                    element={
                      <RedirectIfAuthenticated>
                        <WelcomePage />
                      </RedirectIfAuthenticated>
                    }
                  />
                  <Route
                    path="/login"
                    element={
                      <RedirectIfAuthenticated>
                        <AuthPage />
                      </RedirectIfAuthenticated>
                    }
                  />
                  <Route
                    path="/member-required-actions"
                    element={<MemberRequiredActionsPage />}
                  />

                  <Route element={<RedirectIfNotAuthenticated />}>
                    <Route path="/home" element={<DashboardPage />} />

                    {isFeatureEnabled(
                      FeatureToggleIdentifier.ENABLE_TRENDED_VITALS
                    ) && (
                      <Route
                        path="/home/health-score"
                        element={<HealthScoreLayout />}
                      >
                        <Route index element={<HealthScorePage />} />
                        <Route
                          path="lifestyle-questionnaire"
                          element={<LifeStyleQuestionnaire />}
                        />
                        <Route
                          path="health-history-questionnaire"
                          element={<HealthHistoryQuestionnaire />}
                        />
                        <Route
                          path="mental-wellbeing-questionnaire"
                          element={<MentalWellbeingQuestionnaire />}
                        />
                        <Route
                          path="blood-pressure-manual-entry"
                          element={<BloodPressureManualEntry />}
                        />
                        <Route
                          path="heart-rate-manual-entry"
                          element={<HeartRateManualEntry />}
                        />
                        <Route
                          path="breathing-rate-manual-entry"
                          element={<BreathingRateManualEntry />}
                        />
                      </Route>
                    )}

                    <Route
                      path="/home/my-health/health-profile"
                      element={
                        isTrendedVitalsEnabled ? (
                          <MyHealthProfilePage />
                        ) : (
                          <MyHealthPage />
                        )
                      }
                    />
                    <Route
                      path="/home/my-health/health-record"
                      element={
                        isTrendedVitalsEnabled ? (
                          <MyHealthRecordPage />
                        ) : (
                          <MyHealthPage />
                        )
                      }
                    />
                    <Route path={myContactDetailsUrl} element={myContactPage} />
                    <Route
                      path={communicationPreferencesUrl}
                      element={<CommunicationPreferencesPage />}
                    />
                    <Route
                      path="/home/help/about-app"
                      element={<AboutAppPage />}
                    />

                    {isFeatureEnabled(
                      FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
                    ) && (
                      <>
                        <Route
                          path="/home/get-medical-assistance/vouchers"
                          element={<VoucheringFlow />}
                        />

                        <Route
                          path="/home/more/my-carecard-vouchers"
                          element={<MyCareCardVouchersPage />}
                        />
                      </>
                    )}

                    <Route
                      path="/home/help/about-app/developer-mode"
                      element={<FullscreenLoadingIndicator />}
                    />

                    {isFeatureEnabled(
                      FeatureToggleIdentifier.ENABLE_DEBUG_CONSOLE_LOGS
                    ) && (
                      <Route
                        path="/home/more/healthkit-debug"
                        element={<HealthKitDebugPage />}
                      />
                    )}

                    <Route
                      path="/home/more/get-support"
                      element={<GetSupport />}
                    />

                    <Route
                      path="/home/help/about-app/open-source-licenses"
                      element={<OpenSourceLicensesPage />}
                    />

                    <Route
                      path="/home/my-benefits/benefits"
                      element={<MyBenefitsPage />}
                    />

                    <Route element={<RedirectIfNotB2BMember />}>
                      <Route
                        path="/home/my-benefits/membership"
                        element={<MyBenefitsPage />}
                      />
                      <Route
                        path="/home/memberships/:membershipIdentifier"
                        element={<MyProfileAndMembershipPage />}
                      />
                      {/* <Route
                        path="/home/memberships/:membershipIdentifier"
                        element={<MembershipDetailPage />}
                      /> */}
                      <Route
                        path="/home/memberships/:membershipIdentifier/contact"
                        element={<MembershipContactDetailsPage />}
                      />
                      <Route
                        path="/home/memberships/:membershipIdentifier/manage"
                        element={<MembershipManagementPage />}
                      />
                      <Route
                        path="/home/memberships/:membershipIdentifier/terms"
                        element={<MembershipTermsPage />}
                      />
                      <Route
                        path="/home/memberships/:membershipIdentifier/policy-documents"
                        element={<MembershipPolicyDocumentsPage />}
                      />
                      <Route
                        path="/home/memberships/:membershipIdentifier/*"
                        element={<Navigate to="/home" replace />}
                      />
                    </Route>

                    <Route
                      path="/home/get-medical-assistance"
                      element={<MedicalAssistancePage />}
                    />

                    {isFeatureEnabled(
                      FeatureToggleIdentifier.ENABLE_INTERCARE_MEDICAL_ASSISTANCE
                    ) && (
                      <>
                        <Route
                          path="/home/get-medical-assistance/intercare"
                          element={<InterCareMedicalAssistanceFlow />}
                        />

                        <Route
                          path="/home/get-medical-assistance/intercare/payment-failure"
                          element={<PaymentFailure />}
                        />

                        <Route
                          path="/home/get-medical-assistance/intercare/:id"
                          element={<InterCareMedicalAssistanceFlow />}
                        />
                      </>
                    )}
                    {isFeatureEnabled(
                      FeatureToggleIdentifier.ENABLE_THIRD_PARTY_AUTHENTICATION
                    ) && (
                      <>
                        <Route
                          path="/home/get-medical-assistance/third-party"
                          element={<ThirdPartyMedicalAssistanceFlow />}
                        />
                      </>
                    )}

                    {isFeatureEnabled(
                      FeatureToggleIdentifier.ENABLE_CHAT_BOT
                    ) && (
                      <Route
                        path="/home/sweet-life/chat/:chatBotType"
                        element={<ChatBotPage />}
                      />
                    )}

                    {isFeatureEnabled(
                      FeatureToggleIdentifier.ENABLE_PATHOLOGY_PANELS
                    ) && (
                      <>
                        <Route
                          path="/home/more/my-purchases"
                          element={<MyPurchasesPage />}
                        />
                        <Route
                          path="/home/get-medical-assistance/pathology"
                          element={<PathologyFlow />}
                        />
                        <Route
                          path="/home/get-medical-assistance/pathology/results-breakout/"
                          element={<PathologyResultsBreakoutPage />}
                        />
                      </>
                    )}

                    <Route
                      path="/home/my-profile"
                      element={
                        <RedirectIfNotRetailMember>
                          <MyProfilePage />
                        </RedirectIfNotRetailMember>
                      }
                    />

                    <Route
                      path="home/get-medical-assistance/unsupported"
                      element={<MedicalAssistanceUnsupportedPage />}
                    />
                    <Route path="/home/more" element={<MorePage />} />
                    <Route
                      path="/home/health-measurement/:healthMeasurementIdentifier"
                      element={<HealthMeasurementPage />}
                    />
                    <Route
                      path="/home/health-measurement/:healthMeasurementIdentifier/detail"
                      element={<HealthMeasurementDetailPage />}
                    />

                    <Route
                      path="/binah-scan"
                      element={
                        <BinahScanFlow
                          onCompletedFlow={() =>
                            navigate(
                              isTrendedVitalsEnabled
                                ? "/home/health-score"
                                : "/home"
                            )
                          }
                        />
                      }
                    />
                    <Route
                      path="/binah-scan/native"
                      element={
                        <BinahScanFlowNative
                          onCompletedFlow={() =>
                            navigate(
                              isTrendedVitalsEnabled
                                ? "/home/health-score"
                                : "/home"
                            )
                          }
                        />
                      }
                    />
                    <Route path="/home/messages" element={<MessagesPage />} />
                    <Route
                      path="/home/messages/:messageId"
                      element={<MessageDetailsPage />}
                    />
                  </Route>

                  {/* Logged-out users hitting an unknown /home/* path land on Welcome (/) rather than /login, so the pre-auth 404 flow stays unchanged. */}
                  <Route element={<RedirectIfNotAuthenticated to="/" />}>
                    <Route path="/home/*" element={<NotFoundPage />} />
                  </Route>

                  <Route
                    path="/auth-session-error"
                    element={<HandleExpiredSession />}
                  />
                  <Route
                    path="/playground/*"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        {PlaygroundPage ? (
                          <PlaygroundPage changeMaxWidth={changeMaxWidth} />
                        ) : (
                          <Navigate to="/" replace />
                        )}
                      </Suspense>
                    }
                  />
                  {/* Legacy shortlink: /pathology is linked externally and must keep redirecting to /. Unknown paths fall through to NotFoundPage. */}
                  <Route
                    path="/pathology"
                    element={
                      <Navigate to="/" state={{ isRedirected: true }} replace />
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                  <Route
                    path="/public-binah-scan"
                    element={
                      <Suspense fallback={<SuspenseFallback />}>
                        <PublicBinahScanFlow
                          onCompletedFlow={() => navigate("/public-binah-scan")}
                        />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/get-help/log-query"
                    element={<CollectUserQueryFlow />}
                  />
                  <Route path="/update-app" element={<MaintenancePage />} />
                  <Route
                    path="/unsubscribe"
                    element={
                      <UnsubscribePage changeMaxWidth={changeMaxWidth} />
                    }
                  />
                  {isFeatureEnabled(
                    FeatureToggleIdentifier.ENABLE_PATHOLOGY_PANELS
                  ) && (
                    <>
                      <Route
                        path="/home/get-medical-assistance/pathology/payment-redirect"
                        element={<PaymentRedirect />}
                      />
                    </>
                  )}

                  {isFeatureEnabled(
                    FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
                  ) && (
                    <>
                      <Route
                        path="/home/get-medical-assistance/vouchers/payment-redirect"
                        element={<PaymentRedirect />}
                      />
                    </>
                  )}

                  {isFeatureEnabled(
                    FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS
                  ) && (
                    <Route
                      path="/home/more/my-discount-vouchers"
                      element={<MyDiscountVouchersPage />}
                    />
                  )}

                  <Route
                    path="/home/get-medical-assistance/consultation"
                    element={<InterCareMedicalAssistanceFlow />}
                  />
                </Routes>
                <Routes>
                  <Route path="/get-help" element={<GetHelpModal />} />
                </Routes>

                {from && (
                  <Routes>
                    <Route
                      path="/home/get-medical-assistance/prompt"
                      element={<MedicalAssistancePage showInformationPrompt />}
                    />
                  </Routes>
                )}
              </>
            )}
          </RedirectHandler>
        </AppUpdateHandler>
      </MaxWidthContainer>
    </ScrollToTop>
  );
}
